import { Box, CircularProgress } from '@mui/material';
import { getChartOfferForm } from 'api/chart';
import EmptyData from 'components/EmptyData';
import OwnerCertificationChart from 'components/chart/OwnerCertificationChart';
import { useCallback, useEffect, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';
import {
  ChartOfferForm,
  ChartOfferFormParams,
  ChartOfferFormResponse,
  ChartOption,
  TradeType,
} from 'types/chart';
import { getMinMax, getRangePrice } from 'utils/formatUtil';

interface Props {
  data?: ChartOfferFormResponse[];
}

OwnerCertificationChartPage.defaultProps = {
  data: [],
};

function OwnerCertificationChartPage(props?: Props) {
  const location = useLocation();
  const { search } = location;
  const parameter = new URLSearchParams(search);

  const [params, setParams] = useState<ChartOfferFormParams>({
    complexId: 0,
    layoutPublicAreaType: '',
    tradeType: 'UNKNOWN',
  });
  const [filter, setFilter] = useState({
    hopePrice: undefined,
  });
  const { complexId, layoutPublicAreaType, tradeType } = params;
  const { hopePrice } = filter;
  const [chartData, setChartData] = useState<ChartOfferForm[]>([]);
  const [range, setRange] = useState<number>(0);
  const [summaryInfo, setSummaryInfo] = useState({
    totalVerificationRate: 0,
    totalOfferCount: 0,
    myHomeRanking: 0,
  });
  const [isChartEmpty, setIsChartEmpty] = useState(false);
  const [chartLoading, setChartLoading] = useState(false);
  const [chartOption, setChartOption] = useState<ChartOption>({
    tooltipActive: false,
    xAxisProps: {
      interval: 'preserveEnd',
    },
    yAxisProps: {
      type: 'category',
    },
  });
  const [chartKey, setChartKey] = useState('');

  const urlComplexId = parameter.get('complexId');
  const urlLayoutPublicAreaType = parameter.get('layoutPublicAreaType');
  const urlTradeType = parameter.get('tradeType');
  const urlHopePrice = Number(parameter.get('hopePrice'));

  const isVisibleLayoutAlert = !layoutPublicAreaType && !props;
  const isEmptyData = !chartLoading && isChartEmpty;
  const isVisibleChart = !chartLoading && !isChartEmpty;

  const initWebViewMessageListener = () => {
    if (!isMobile) {
      return;
    }

    if (!window.ReactNativeWebView) {
      return;
    }

    const webviewListener = (event: any) => {
      try {
        const { payload, type } = JSON.parse(event.data);

        switch (type) {
          case 'setComplex':
            setParams((prev) => ({
              ...prev,
              complexId: Number(payload.complexId),
            }));
            break;
          case 'setLayoutPublicAreaType':
            if (payload.layoutPublicAreaType) {
              setParams((prev) => ({
                ...prev,
                layoutPublicAreaType: payload.layoutPublicAreaType,
              }));
            }
            break;
          case 'setTradeType':
            setParams((prev) => ({
              ...prev,
              tradeType: payload.tradeType,
            }));
            break;
          case 'setHopePrice':
            setFilter((prev) => ({
              ...prev,
              hopePrice: payload.hopePrice,
            }));
            break;
          case 'dismissChartTooltip':
            setChartOption((prev) => ({
              ...prev,
              tooltipActive: false,
            }));
            break;
          default:
            break;
        }
      } catch (error) {
        console.log('json parse error', error);
      }
    };

    document.addEventListener('message', webviewListener);
    window.addEventListener('message', webviewListener);
  };

  const fetchData = async (params: ChartOfferFormParams, price?: number) => {
    let data: ChartOfferFormResponse[] = [];
    if (params.complexId === 0) {
      return;
    }
    console.log('params: ', params);
    setChartLoading(true);
    if (props?.data?.length) {
      data = props.data;
    } else {
      const response = await getChartOfferForm(params);
      data = response.data?.data || [];
    }
    setChartLoading(false);
    console.log('data: ', data);

    setIsChartEmpty(data.length === 0);

    if (data.length > 0) {
      const { formattedData, totalOfferCount, totalVerificationCount } =
        formatData(data);
      const totalVerificationRate = calculateVerificationRate(
        totalOfferCount,
        totalVerificationCount,
      );
      const updatedOfferCount = totalOfferCount + 1;

      setSummaryInfo((prev) => ({
        ...prev,
        totalOfferCount: updatedOfferCount,
        totalVerificationRate,
      }));

      updateXInterval(formattedData);

      const prices = formattedData.map((item) => item.price);

      const { min, max } = getMinMax(prices);
      const tickCount = 5;
      const { ticks, range: rangeData } = getRangePrice({
        min,
        max,
        tickCount,
      });

      setRange(rangeData);

      if (ticks) {
        const result = createChartData(ticks, formattedData, price);
        const myHomeRanking = calculateHomeRanking(
          formattedData,
          updatedOfferCount,
          price,
        );

        setChartData(result);

        setSummaryInfo((prev) => ({
          ...prev,
          myHomeRanking,
        }));
      }
    }
  };

  const formatData = (data: ChartOfferFormResponse[]) => {
    let totalOfferCount = 0;
    let totalVerificationCount = 0;

    const formattedData: ChartOfferForm[] = data.map((value) => {
      totalOfferCount += value.offerCount;
      totalVerificationCount += value.verificationCount;

      return {
        ...value,
        unverifiedOfferCount: value.offerCount - value.verificationCount,
        isHopePrice: false,
        isLast: false,
      };
    });

    return { formattedData, totalOfferCount, totalVerificationCount };
  };

  const calculateVerificationRate = (
    totalOfferCount: number,
    totalVerificationCount: number,
  ) => {
    return totalOfferCount && totalVerificationCount
      ? Number(((totalVerificationCount / totalOfferCount) * 100).toFixed(2))
      : 0;
  };

  const updateXInterval = (formattedData: ChartOfferForm[]) => {
    const offerCounts = formattedData.map((item) => item.offerCount);
    const maxOfferCount = Math.max(...offerCounts);

    if (maxOfferCount < 5) {
      if (maxOfferCount % 2 === 1) {
        setChartOption((prev) => ({
          ...prev,
          xInterval: 3,
        }));
      } else {
        setChartOption((prev) => ({
          ...prev,
          xInterval: 1,
        }));
      }
    }
  };

  const createChartData = (
    ticks: number[],
    formattedData: ChartOfferForm[],
    price?: number,
  ) => {
    return ticks.map((value, index) => {
      const nextTick = ticks[index + 1] || Infinity;
      const rangeItems = formattedData.filter(
        (item) => item.price >= value && item.price < nextTick,
      );
      const isLast = index === ticks.length - 1;
      const isHopePrice =
        price !== undefined && price >= value && price < nextTick;

      if (rangeItems.length > 0 || isHopePrice) {
        const sumOfferCount = rangeItems.reduce(
          (acc, item) => acc + item.offerCount,
          0,
        );
        const sumVerificationCount = rangeItems.reduce(
          (acc, item) => acc + item.verificationCount,
          0,
        );
        const sumUnverifiedOfferCount = rangeItems.reduce(
          (acc, item) => acc + (item.unverifiedOfferCount || 0),
          0,
        );
        const verificationRate = Number(
          ((sumVerificationCount / sumOfferCount) * 100).toFixed(2),
        );

        return {
          price: value,
          offerCount: sumOfferCount,
          verificationCount: sumVerificationCount,
          verificationRate,
          unverifiedOfferCount: sumUnverifiedOfferCount,
          isHopePrice,
          isLast,
        };
      }

      return {
        price: value,
        offerCount: 0,
        verificationCount: 0,
        verificationRate: 0,
        unverifiedOfferCount: 0,
        isHopePrice: false,
        isLast,
      };
    });
  };

  const calculateHomeRanking = (
    formattedData: ChartOfferForm[],
    totalOfferCount: number,
    price?: number,
  ) => {
    let cumulativeOfferCount = 0;
    let updatedFormattedData = [...formattedData];

    for (let i = 0; i < updatedFormattedData.length; i += 1) {
      if (
        price !== undefined &&
        price >= updatedFormattedData[i].price &&
        price < (updatedFormattedData[i + 1]?.price || Infinity)
      ) {
        cumulativeOfferCount += updatedFormattedData[i].offerCount;
        updatedFormattedData[i] = {
          ...updatedFormattedData[i],
          isHopePrice: true,
        };
        return totalOfferCount - cumulativeOfferCount;
      }

      cumulativeOfferCount += updatedFormattedData[i].offerCount;
    }

    return 0;
  };

  useEffect(() => {
    initWebViewMessageListener();
  }, []);

  useEffect(() => {
    fetchData(params, hopePrice);

    const key = `owner-certification-chart-${complexId}-${layoutPublicAreaType}-${tradeType}-${hopePrice}`;
    setChartKey(key);
  }, [params, filter]);

  useEffect(() => {
    const urlParms = {
      complexId: Number(urlComplexId) || 0,
      layoutPublicAreaType: urlLayoutPublicAreaType || '',
      tradeType: (urlTradeType as TradeType) || 'TRADE',
    };
    const isAllUrlParams =
      urlComplexId && urlLayoutPublicAreaType && urlTradeType && urlHopePrice;
    if (isAllUrlParams) {
      fetchData(urlParms, urlHopePrice);
    }
  }, [search]);

  useEffect(() => {
    console.log('fetchDatastart: ', params);
    fetchData({
      complexId: 11612,
      layoutPublicAreaType: '24',
      tradeType: 'TRADE',
    });
    // if (params) {
    //   const urlParms = {
    //     complexId: Number(urlComplexId) || 0,
    //     layoutPublicAreaType: urlLayoutPublicAreaType || '',
    //     tradeType: (urlTradeType as TradeType) || 'TRADE',
    //   };
    //   fetchData(urlParms);
    // }
  }, []);

  const getMyHomeRanking = useCallback(() => {
    switch (summaryInfo.myHomeRanking) {
      case 0:
        return '가장 낮은 가격이에요';
      case 1:
        return '가장 높은 가격이에요';

      default:
        return `${summaryInfo.myHomeRanking}번째로 높은 가격이에요`;
    }
  }, [summaryInfo, hopePrice]);

  return (
    <Container>
      {isVisibleLayoutAlert && (
        <EmptyData
          minHeight="220px"
          text="먼저 평형을 선택해주세요!"
          background="#FAFAFA"
          borderRadius="8px"
        />
      )}
      {chartLoading && (
        <LoadingWrapper>
          <CircularProgress sx={{ color: '#9E9E9E' }} />
        </LoadingWrapper>
      )}
      {isEmptyData && (
        <EmptyData
          minHeight="220px"
          text="네이버에 해당 평형의 데이터가 없습니다."
          background="#FAFAFA"
          borderRadius="8px"
        />
      )}
      {isVisibleChart && (
        <>
          <CertificationBox>
            <Box component="span">우리 집 집주인 인증 비율은 </Box>
            <Box component="span" color="#333" fontWeight={700}>
              {summaryInfo.totalVerificationRate}%
            </Box>
            <Box component="span">입니다</Box>
          </CertificationBox>
          <OwnerCertificationChart
            chartData={chartData}
            chartOption={chartOption}
            chartKey={chartKey}
            range={range}
            onChartClick={() =>
              setChartOption((prev) => ({ ...prev, tooltipActive: true }))
            }
            onChartMouseLeave={() =>
              setChartOption((prev) => ({ ...prev, tooltipActive: false }))
            }
          />

          {hopePrice !== undefined && (
            <Box
              color="#9E9E9E"
              fontWeight={700}
              fontSize="14px"
              textAlign="center"
            >
              매물 {summaryInfo.totalOfferCount}개 중 {getMyHomeRanking()}
            </Box>
          )}
        </>
      )}
    </Container>
  );
}

export default OwnerCertificationChartPage;

const Container = styled.div``;

const CertificationBox = styled(Box)`
  width: 100%;
  border-radius: 4px;
  background: #fafafa;
  padding: 7px 0;
  color: var(--grey-warm-5, #9e9e9e);
  font-size: 14px;
  font-weight: 500;
  line-height: 16px;
  letter-spacing: -0.28px;
  text-align: center;
  margin-bottom: 10px;
`;

const LoadingWrapper = styled.div`
  min-height: 220px;
  display: flex;
  justify-content: center;
  align-items: center;
`;
